import { BulkCreateMockupsResponse } from '@wix/ambassador-suppliershub-marketplace-mockups-v1-mockup/types';
import { useMutation } from '@tanstack/react-query';
import { actions } from 'api/actions';
import { SHUB_APP_DEF_ID } from 'config/apps';
import { useCallback, useEffect, useRef } from 'react';
import { useAppStore } from 'stores/appStore';
import { useMockupStore } from 'stores/mockupStore';
import { GeneratedMockup } from 'types/mockups';
import { bulkMockupResponseHasFailure, shouldStopMockupLoading } from 'utils/mockup-generation';
import { getSelectedProductIds } from 'utils/product-selection';
import { DuplexerEvent } from 'utils/duplexer';
import { SelectableProduct } from 'types/selectable-product';

export type GenerateMockupsInput =
  | {
      type: 'localFile';
      file: File;
    }
  | {
      type: 'siteImage';
      mediaUrl: string;
    };

type MutationContext = {
  generationId: number;
};

interface UseMockupGenerateResult {
  generateMockups: (input: GenerateMockupsInput) => void;
}

export const useMockupGenerate = (): UseMockupGenerateResult => {
  const { metaSiteId } = useAppStore.getState();

  const selectedProducts = useAppStore((state) => state.selectedProducts);
  const mockups = useMockupStore((state) => state.mockups);
  const setMockups = useMockupStore((state) => state.setMockups);
  const setIsLoading = useMockupStore((state) => state.setIsLoading);
  const setGenerationFailed = useMockupStore((state) => state.setGenerationFailed);
  const bulkResponseRef = useRef<BulkCreateMockupsResponse | undefined>();
  const generationIdRef = useRef(0);

  const duplexer = useRef(
    new DuplexerEvent<GeneratedMockup>(`shub-mockups-${metaSiteId}`, 'mockup_updated', SHUB_APP_DEF_ID),
  );

  const stopLoadingIfComplete = useCallback(() => {
    const currentSelectedProductIds = getSelectedProductIds(useAppStore.getState().selectedProducts);
    const currentMockups = useMockupStore.getState().mockups;

    if (shouldStopMockupLoading(currentSelectedProductIds, currentMockups, bulkResponseRef.current)) {
      setIsLoading(false);
    }
  }, [setIsLoading]);

  const resetImageAfterMockupFailure = useCallback(() => {
    useAppStore.setState({ localFile: undefined, mediaUrl: undefined });
    setMockups({});
    setGenerationFailed(true);
    setIsLoading(false);
  }, [setGenerationFailed, setIsLoading, setMockups]);

  const { mutate } = useMutation<BulkCreateMockupsResponse | undefined, Error, GenerateMockupsInput, MutationContext>({
    onMutate: (input) => {
      const generationId = generationIdRef.current + 1;
      generationIdRef.current = generationId;
      bulkResponseRef.current = undefined;
      setMockups({});
      setGenerationFailed(false);
      setIsLoading(true);

      if (input.type === 'localFile') {
        useAppStore.setState({ localFile: input.file, mediaUrl: undefined });
      } else {
        useAppStore.setState({ localFile: undefined, mediaUrl: input.mediaUrl });
      }

      return { generationId };
    },
    mutationFn: async (input) => {
      duplexer.current.subscribe((mockup) => {
        const _mockups = useMockupStore.getState().mockups;
        setMockups({ ..._mockups, [mockup.productId]: mockup });
      });

      const currentSelectedProducts: SelectableProduct[] = useAppStore.getState().selectedProducts;
      const mediaUrl = input.type === 'localFile' ? await actions.uploadSiteMediaFile(input.file) : input.mediaUrl;
      return actions.generateMockups(mediaUrl, currentSelectedProducts);
    },
    onSuccess: (response, _input, context) => {
      if (context.generationId !== generationIdRef.current) {
        return;
      }

      bulkResponseRef.current = response;

      if (bulkMockupResponseHasFailure(response)) {
        resetImageAfterMockupFailure();
        return;
      }

      stopLoadingIfComplete();
    },
    onError: (_error, _input, context) => {
      if (context?.generationId !== generationIdRef.current) {
        return;
      }

      resetImageAfterMockupFailure();
    },
  });

  useEffect(() => {
    stopLoadingIfComplete();
  }, [mockups, selectedProducts, stopLoadingIfComplete]);

  const generateMockups = useCallback(
    (input: GenerateMockupsInput) => {
      mutate(input);
    },
    [mutate],
  );

  return { generateMockups };
};
